// Edit one ingredient or utensil line: substitute, amount, unit / count, scaling.
// Port of Swift InstructionLinkEditingPopover, plus a Cancel (Swift had none).
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sheet from './Sheet';
import { Button, Caption, Chip, Field, Row, Stepper, ToggleRow } from './ui';
import { displayFixedPoint } from '../logic/format';
import { parseAmount } from '../logic/quantity';
import { SEED_UNIT, SYSTEM_UNITS, type UnitRef } from '../logic/units';
import { useAllIngredients, useAllUtensils, useLibrary } from '../store/library';
import { useSettings } from '../store/settings';
import type { IngredientLine, UtensilLine } from '../types';

export type EditableLine = { kind: 'ingredient'; line: IngredientLine } | { kind: 'utensil'; line: UtensilLine };

export default function LineEditorSheet({ target, onClose, onSave }: { target: EditableLine | null; onClose: () => void; onSave: (line: EditableLine) => void }) {
  const { t } = useTranslation();
  const ingredients = useAllIngredients();
  const utensils = useAllUtensils();
  const customUnits = useLibrary(s => s.units);
  const unitSystem = useSettings(s => s.unitSystem);
  const [draft, setDraft] = useState<EditableLine | null>(target);
  const [amountText, setAmountText] = useState('');

  useEffect(() => {
    setDraft(target);
    if (target?.kind === 'ingredient') setAmountText(displayFixedPoint(target.line.amount));
  }, [target]);

  if (!draft) return null;

  if (draft.kind === 'ingredient') {
    const line = draft.line;
    const current = ingredients[line.ingredientId];
    const similar = current?.similarGroup ? Object.values(ingredients).filter(i => i.similarGroup === current.similarGroup) : current ? [current] : [];
    const parsed = parseAmount(amountText);
    const unitOptions: UnitRef[] = [null, ...SYSTEM_UNITS[unitSystem].map(k => ({ seed: k })), ...Object.values(customUnits).sort((a, b) => a.title.localeCompare(b.title)).map(u => ({ custom: u.id }))];
    // Keep a unit from another system selectable so editing doesn't silently drop it.
    if (line.unit && !unitOptions.some(u => JSON.stringify(u) === JSON.stringify(line.unit))) unitOptions.push(line.unit);
    const unitLabel = (u: UnitRef) => (!u ? t('recipe.noUnit') : 'seed' in u ? SEED_UNIT[u.seed].symbol : customUnits[u.custom]?.symbol || customUnits[u.custom]?.title || '?');
    return (
      <Sheet visible={!!target} onClose={onClose} title={current?.title ?? t('recipe.editItem')}>
        {similar.length > 1 ? (
          <>
            <Caption>{t('recipe.substitute')}</Caption>
            <Row>
              {similar.map(i => (
                <Chip key={i.id} label={i.title} selected={i.id === line.ingredientId} onPress={() => setDraft({ kind: 'ingredient', line: { ...line, ingredientId: i.id } })} />
              ))}
            </Row>
          </>
        ) : null}
        <Field label={t('recipe.amount')} value={amountText} onChangeText={setAmountText} keyboardType="decimal-pad" placeholder="1 ½, 1.5, 1,5" />
        <Caption>{t('recipe.unitLabel')}</Caption>
        <Row>
          {unitOptions.map(u => (
            <Chip key={JSON.stringify(u)} label={unitLabel(u)} selected={JSON.stringify(u) === JSON.stringify(line.unit)} onPress={() => setDraft({ kind: 'ingredient', line: { ...line, unit: u } })} />
          ))}
        </Row>
        <Button
          label={t('common.save')}
          icon="check"
          disabled={!parsed}
          onPress={() => {
            if (parsed) onSave({ kind: 'ingredient', line: { ...line, amount: parsed } });
          }}
        />
        <Button label={t('common.cancel')} variant="secondary" onPress={onClose} />
      </Sheet>
    );
  }

  const line = draft.line;
  const set = (patch: Partial<UtensilLine>) => setDraft({ kind: 'utensil', line: { ...line, ...patch } });
  return (
    <Sheet visible={!!target} onClose={onClose} title={utensils[line.utensilId]?.title ?? t('recipe.editItem')}>
      <Caption>{t('recipe.amount')}</Caption>
      <Stepper label={t('recipe.amount')} value={line.amount} onChange={v => set({ amount: v })} min={1} max={99} />
      <ToggleRow label={t('recipe.doNotScale')} value={line.scaleEvery === null} onChange={v => set({ scaleEvery: v ? null : 1 })} />
      {line.scaleEvery !== null ? (
        <>
          <Caption>{t('recipe.scaleEvery', { n: line.scaleEvery })}</Caption>
          <Stepper label={t('recipe.scaleEvery', { n: line.scaleEvery })} value={line.scaleEvery} onChange={v => set({ scaleEvery: v })} min={1} max={999} />
        </>
      ) : null}
      <Button label={t('common.save')} icon="check" onPress={() => onSave(draft)} iconOnly />
      <Button label={t('common.cancel')} variant="secondary" onPress={onClose} />
    </Sheet>
  );
}
